import {
	FIELDS_BY_BACKEND_ID,
	PREFERENCE_TYPES,
	DROPDOWN_FIELDS,
	MINMAX_FIELDS,
	BASICS_FIELDS,
	FIELD_TYPES,
	FIELDS,
} from "apps/global_preferences/util/constants";

/**
 * Get the field type (dropdown, minmax, or checkboxes).
 * @param {string} fieldName - the fieldName, one of FIELDS.
 * @return {string} - the fieldType, one of FIELD_TYPES.
 */
const getFieldType = (fieldName) => {
	if (DROPDOWN_FIELDS.has(fieldName)) {
		return FIELD_TYPES.DROPDOWN;
	} else if (MINMAX_FIELDS.has(fieldName)) {
		return FIELD_TYPES.MIN_MAX;
	}

	return FIELD_TYPES.CHECKBOXES;
};

/**
 * PreferenceOptions
 * @typedef {[{ id: number, name: string }]} PreferenceOptions - The array of options
 *
 * ApiPreference
 * @typedef {Object} ApiPreference
 * @property {string} name - The name
 * @property {boolean} bitmask - if the field is bitmasked
 * @property {number} id - The backend id
 * @property {string} any_option - The localized text for the any option,
 * only present when the field allows selecting open to all
 * @property {PreferenceOptions} options - The array of options
 *
 * ApiPreferenceCategory
 * @typedef {Object} ApiPreferenceCategory
 * @property {string} category_id - The category id
 * @property {ApiPreference[]} category_preferences - Array of api preferences
 *
 * A Preference
 * @typedef {Object} Preference
 * @property {string} name - The name
 * @property {boolean} bitmask - if the field is bitmasked
 * @property {number} id - The backend id
 * @property {string} any_option - The localized text for the any option,
 * only present when the field allows selecting open to all
 * @property {PreferenceOptions} options - The array of options
 * @property {string} category - the category the preference belongs to
 * @property {string} fieldName - the field name
 * @property {string} fieldType - the field type (FIELD_TYPES)
 */

/**
 * Transform the API response into a lookup
 * and attach useful properties to the preference like fieldName and prompt
 * so the compoents don't need to compute them
 * @param {ApiPreferenceCategory[]} preferenceCategories - preferenceCategories from the api
 * @return {Object} - the preference lookup: { [preference.id]: preference }
 */
export function createPreferenceLookup(preferenceCategories = []) {
	const transformedPrefs = {};

	preferenceCategories.forEach((category) => {
		category.category_preferences.forEach((preference) => {
			const fieldName = FIELDS_BY_BACKEND_ID[preference.id];

			// sort langauge options so the selected ones are at the top
			if (FIELDS_BY_BACKEND_ID[preference.id] === FIELDS.LANGUAGES) {
				//  yeah you can subtract booleans: true - false = 1...
				preference.options.sort((a, b) => b.is_selected - a.is_selected);
			}

			const transformedPreference = {
				...preference,
				category: category.category_id,
				fieldName,
				fieldType: getFieldType(fieldName),
			};

			transformedPrefs[transformedPreference.id] = transformedPreference;
		});
	});

	return transformedPrefs;
}

/**
 * Given a preference, filter out the options that the user has chosen
 * @param {Preference} preference - the preference
 * @return {PreferenceOptions} - the selected preferences
 */
export function getSelectedOptions(preference) {
	return preference.options.filter((option) => option.is_selected);
}

/**
 * Given a preference's options, get the min and max option
 * @param {PreferenceOptions} options - preference options
 * @return {PreferenceOptions} - the min and max preference options
 */
export const getMinAndMax = (options = [{}, {}]) => {
	const minOptionId = options[0];
	const maxOptionId = options[options.length - 1];

	return [minOptionId, maxOptionId];
};

/**
 * Check if a all of a preference's options are selected
 * @param {number} preferenceId - the preference id
 * @param {PreferenceOptions} options - the preference's options
 * @param {PreferenceOptions} selectedOptions - the preference's selected options
 * @return {boolean} - if all options are selected
 */
export function areAllOptionsSelected(preferenceId, options = [], selectedOptions = []) {
	let allSelected = selectedOptions.length === options.length;

	if (FIELDS_BY_BACKEND_ID[preferenceId] === FIELDS.HEIGHT) {
		const [min, max] = getMinAndMax(options);

		allSelected = min.is_selected && max.is_selected;
	}

	return allSelected;
}

/**
 * format a modifed preference for the api
 * @param {Preference} updatedPreference - the preference to saves
 * @return {Object} - the preference formatted for saving as:
 * { [preferenceid]: { values: [], type: 0|1 } }
 */
export function getSaveableChange(updatedPreference) {
	if (!updatedPreference || updatedPreference.id == null) {
		return {};
	}

	const { fieldName, fieldType, id: preferenceId } = updatedPreference;

	const type = BASICS_FIELDS.has(fieldName) ? PREFERENCE_TYPES.BASICS : PREFERENCE_TYPES.SORT;
	let values = getSelectedOptions(updatedPreference).map(({ id }) => id);

	if (fieldType === FIELD_TYPES.MIN_MAX && values.length === 1) {
		// if a min max field selects the same number e.g. min of 5 and max of 5
		// then add the value twice, since there is only one option and we need to save two values
		values.push(values[0]);
	}

	const minAndMaxHeightAreSelected = FIELDS_BY_BACKEND_ID[preferenceId] === FIELDS.HEIGHT
		&& areAllOptionsSelected(preferenceId, updatedPreference.options);

	if (minAndMaxHeightAreSelected) {
		// clear the preference if the min and max are selected
		values = [0, 0];
	}

	return {
		[updatedPreference.id]: {
			values,
			type,
		},
	};
}

/**
 * Update the preference state with a modified preference
 * @param {number} udpatedPreferenceId - the id of the preference to update
 * @param {number[]} updatedValues - the values of the modified preference
 * @param {Object} preferences - the current preference state
 * @return {Object} - the udpated preference state
 */
export function updatePreferenceState(udpatedPreferenceId, updatedValues, preferences) {
	const updatedValuesLookup = updatedValues.reduce((all, v) => ({ ...all, [v]: true }), {});
	const preference = preferences[udpatedPreferenceId];

	return {
		...preferences,
		[udpatedPreferenceId]: {
			...preference,
			options: (preference.options || []).map((option) => ({
				...option,
				is_selected: !!updatedValuesLookup[option.id],
			})),
		},
	};
}

/**
 * Take a preference object and interpolate some new user choices into it
 * @param {Preference} preference - the preference to modify
 * @param {number[]} choices - the selected options
 * @return {Preference} - the updated preference
 */
export function updatePreferenceChoices(preference, choices) {
	const updatedPreference = { ...preference };
	const updatedPreferenceOptions = updatedPreference.options.map((option) => ({
		...option,
		is_selected: choices.includes(option.id),
	}));

	return {
		...updatedPreference,
		options: updatedPreferenceOptions,
	};
}

/**
 * Format the API-style structure into something Checkbox components understand
 * @param {Preference} preference - the preference
 * @return {{ checkboxLabels: string[], checkboxOrder: number[] }}
 * checkbox labels and order
 */
export function getCheckboxParams(preference) {
	const checkboxLabels = {};
	const checkboxOrder = [];

	preference.options.forEach((option) => {
		checkboxLabels[option.id] = option.name;
		checkboxOrder.push(option.id);
	});

	return {
		checkboxLabels,
		checkboxOrder,
	};
}

/**
 * Format the API-style structure into something OkSelect components understand
 * @param {Preference} preference - the preference
 * @return {{ name: string, value: string, disabled: (boolean|undefined) }[]}
 * dropdown params with an mdash placeholder prepended
 */
export function getDropdownParams(preference) {
	const params = preference.options.map((option) => {
		return {
			name: option.name,
			value: option.id,
		};
	});

	// Prepend placeholder
	params.unshift({ name: "&mdash;", value: "&mdash;", disabled: true });

	return params;
}



// WEBPACK FOOTER //
// ./src/apps/global_preferences/util/helpers.js